import { AsyncPipe, NgIf } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormlySelectOption, FormlySelectOptionsPipe } from '@ngx-formly/core/select';
import { TransformPipe } from '@core/pipes';
import { getFormlyFieldValue } from '../../../utils/formlyfield.helper';

/**
 * Builds a chip label for a selected array item that has no matching select
 * option (e.g. a Formly Repeat row object like a package). Prefers a caller
 * formatter at `props.selectionLabel`; otherwise falls back to a readable join.
 */
function formatSelectionItem(item: unknown, field?: FormlyFieldConfig): string {
  const formatter = field?.props?.['selectionLabel'] as ((item: unknown) => string) | undefined;
  if (typeof formatter === 'function') {
    return formatter(item);
  }

  if (item && typeof item === 'object') {
    // Generic, unambiguous fallback for object rows (e.g. a package box): key: value pairs.
    return Object.entries(item as Record<string, unknown>)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  }

  return String(item);
}

@Component({
  selector: 'kt-preview-selection',
  templateUrl: './preview.component.html',
  styles: [
    `
      .label {
        color: #3f51b5;
        text-wrap: nowrap;
      }
    `,
  ],
  imports: [AsyncPipe, FormlySelectOptionsPipe, MatChipsModule, MatIconModule, NgIf, TransformPipe],
})
export class PreviewSelectionComponent {
  readonly field = input.required<FormlyFieldConfig>();
  readonly showPreview = input<boolean>(false);
  readonly canRemove = input<boolean>(false);
  readonly shorten = input<boolean>(true);
  readonly remove = output<any>();

  // Resolves the field value, covering Formly Repeat (fieldArray) fields that
  // carry no formControl in the panel. Called from the template so it is
  // re-evaluated each change-detection pass (preserving the previous reactivity).
  resolveValue(field: FormlyFieldConfig): unknown {
    return getFormlyFieldValue(field);
  }

  // NOTE: invoked unbound via the `transform` pipe — must not rely on `this`.
  showSelected(value: unknown | unknown[], options: FormlySelectOption[], f: FormlyFieldConfig): FormlySelectOption[] {
    if (value === undefined || value === null) {
      return [];
    }

    if (Array.isArray(value)) {
      if (options?.length) {
        return options.filter(o => value.includes(o.value));
      }

      // No select options (e.g. a Repeat of package objects): render one chip per row.
      return value
        .filter(item => item !== null && item !== undefined)
        .map(item => ({ label: formatSelectionItem(item, f), value: item as any }));
    }

    const opt = options.find(o => o.value === value);
    if (opt) {
      return [opt];
    }

    return [{ label: String(value), value: value as any }] as FormlySelectOption[];
  }
}
